import type { ReactNode } from 'react';
import {
  CartaoIndicador,
  CelulaDeTotal,
  EstadoCarregando,
  EstadoErro,
  EstadoVazio,
  Tabela,
  ValorFiscal,
  ValorPercentual,
  type ColunaTabela,
} from '../ui';
import { usePainelDoCliente } from '../../hooks/usePainel';
import { mensagemDoErro } from '../../servicos/erros';
import { formatarCompetencia } from '../../servicos/formato';
import type { MesDoPainel, PainelDoCliente } from '../../servicos/painel';
import {
  CartaoMesesAproveitados,
  NotaDeMetricasIndisponiveis,
  SeloDeContrato,
  TituloDeSecao,
} from './comum';

/**
 * Dashboard do cliente — `GET /api/icms/dashboard/clientes/:id/:ano`.
 *
 * É uma **série de 12 meses** (RN-167), diferente do dashboard de participante,
 * que é uma lista de linhas cliente × fornecedor. Os dois não foram forçados no
 * mesmo componente: a tabela do cliente tem competência como cabeçalho de linha
 * e uma coluna de origem do deságio que não existe do outro lado.
 */

/** RN-150 — de onde saiu o deságio do mês, em português. */
const ROTULO_ORIGEM_DESAGIO: Readonly<Record<string, string>> = {
  SPED_MATERIALIZADO: 'SPED fechado',
  CONTRATO: 'Do contrato',
  INDEFINIDO: 'Não definido',
};

const COLUNAS_COMPETENCIAS: readonly ColunaTabela<MesDoPainel>[] = [
  {
    chave: 'competencia',
    titulo: 'Competência',
    larguraMinima: '8rem',
    celula: (mes) => formatarCompetencia(mes.competencia),
  },
  {
    chave: 'valorUtilizacao',
    titulo: 'Valor de utilização',
    numerica: true,
    celula: (mes) => <ValorFiscal valor={mes.valorUtilizacao} />,
  },
  {
    chave: 'desagio',
    titulo: '% deságio',
    numerica: true,
    // Mês sem contrato chega sem deságio: `null` vira travessão, nunca `0`.
    // Foi exatamente o `null` inesperado que derrubava a tela no legado
    // (dívida #38), e o `0` inventado que a dívida #46 cobra.
    celula: (mes) => <ValorPercentual percentual={mes.desagioVendaPercentual} />,
  },
  {
    chave: 'beneficioEconomico',
    titulo: 'Economia',
    numerica: true,
    celula: (mes) => <ValorFiscal valor={mes.beneficioEconomico} variante="economia" />,
  },
  {
    chave: 'valorPago',
    titulo: 'Valor pago',
    numerica: true,
    celula: (mes) => <ValorFiscal valor={mes.valorPago} />,
  },
  {
    chave: 'fornecedor',
    titulo: 'Fornecedor',
    larguraMinima: '12rem',
    celula: (mes) =>
      mes.fornecedorNome ?? <span className="text-conteudo-secundario">sem fornecedor</span>,
  },
  {
    chave: 'statusContrato',
    titulo: 'Contrato',
    larguraMinima: '11rem',
    celula: (mes) => <SeloDeContrato status={mes.statusContrato} />,
  },
  {
    chave: 'origemDesagio',
    titulo: 'Origem do deságio',
    larguraMinima: '10rem',
    celula: (mes) => (
      <span className="text-conteudo-secundario">
        {ROTULO_ORIGEM_DESAGIO[mes.origemDesagio] ?? mes.origemDesagio}
      </span>
    ),
  },
];

export function PainelCliente(props: {
  readonly ano: number;
  /** O filtro do ano, na linha do "Resumo de …". */
  readonly seletorDeAno?: ReactNode;
}): JSX.Element {
  const painel = usePainelDoCliente(props.ano);

  // O cabeçalho do Resumo sai antes dos dados: trocar o ano não faz o filtro
  // sumir enquanto o ano novo carrega. RN-180 — o SPED é o do ano anterior.
  const cabecalho = (
    <TituloDeSecao
      id="titulo-resumos"
      titulo={`Resumo de ${props.ano}`}
      descricao={`Apurado sobre o SPED de ${props.ano - 1} — o ano fiscal é sempre o anterior ao da operação (RN-180).`}
      {...(props.seletorDeAno === undefined ? {} : { acao: props.seletorDeAno })}
    />
  );

  if (painel.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {cabecalho}
        <EstadoCarregando variante="painel" contexto={`o painel de ${props.ano}`} />
      </div>
    );
  }

  if (painel.isError) {
    return (
      <div className="flex flex-col gap-4">
        {cabecalho}
        <EstadoErro
          titulo="Não foi possível carregar o painel"
          // A mensagem é a que o servidor mandou, nunca um texto fixo (dívida #50,
          // RN-627/RN-628). O que não entra aqui é `detalhe`: mensagem crua de
          // banco vazava nome de tabela e coluna na tela do legado.
          mensagem={mensagemDoErro(painel.error)}
          aoTentarNovamente={() => void painel.refetch()}
        />
      </div>
    );
  }

  return <ClienteCarregado dados={painel.data} cabecalho={cabecalho} />;
}

function ClienteCarregado(props: {
  readonly dados: PainelDoCliente;
  readonly cabecalho: ReactNode;
}): JSX.Element {
  const { dados } = props;

  return (
    <div className="flex flex-col gap-8">
      <section aria-labelledby="titulo-resumos" className="flex flex-col gap-4">
        {props.cabecalho}

        {/*
          Semântica dos totais (RN-010, RN-065): `beneficioEconomico` é o que o
          cliente deixa de pagar — único que leva o verde reservado a economia;
          `valorPago` é o desembolso. Trocar os dois inverte a leitura.
        */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <CartaoIndicador
            rotulo="Valor de utilização"
            valor={<ValorFiscal valor={dados.totais.valorUtilizacao} tamanho="grande" />}
            detalhe="Crédito transferido ao fornecedor no ano."
          />
          <CartaoIndicador
            rotulo="Economia"
            valor={
              <ValorFiscal
                valor={dados.totais.beneficioEconomico}
                variante="economia"
                tamanho="grande"
              />
            }
            detalhe="Quanto a empresa deixou de pagar com o deságio."
          />
          <CartaoIndicador
            rotulo="Valor pago"
            valor={<ValorFiscal valor={dados.totais.valorPago} tamanho="grande" />}
            detalhe="Desembolso da empresa pelos créditos adquiridos."
          />
          <CartaoMesesAproveitados
            meses={dados.totais.mesesAproveitamento}
            metricasIndisponiveis={dados.metricasIndisponiveis}
          />
        </div>
      </section>

      <section aria-labelledby="titulo-competencias" className="flex min-w-0 flex-col gap-4">
        <TituloDeSecao
          id="titulo-competencias"
          titulo="Competências"
          descricao="Cada mês apurado, com o deságio aplicado e o contrato que o cobre."
        />

        {dados.meses.length === 0 ? (
          <EstadoVazio
            titulo={`Nenhuma competência apurada em ${dados.ano}`}
            descricao={`Assim que houver SPED de ${dados.anoSped} processado para esta empresa, os meses aparecem aqui.`}
          />
        ) : (
          <Tabela
            legenda={`Competências apuradas em ${dados.ano}, sobre o SPED de ${dados.anoSped}`}
            colunas={COLUNAS_COMPETENCIAS}
            dados={dados.meses}
            chaveDaLinha={(mes) => mes.competencia}
            chaveDeCabecalhoDaLinha="competencia"
            rodape={
              <tr>
                <CelulaDeTotal cabecalhoDaLinha>Total do ano</CelulaDeTotal>
                <CelulaDeTotal numerica>
                  <ValorFiscal valor={dados.totais.valorUtilizacao} />
                </CelulaDeTotal>
                {/* Percentual não soma: a média ponderada do ano não é um total,
                    e a resposta não a traz. Travessão, não uma conta inventada
                    aqui (RN-615). */}
                <CelulaDeTotal numerica>
                  <span aria-hidden="true">—</span>
                </CelulaDeTotal>
                <CelulaDeTotal numerica>
                  <ValorFiscal valor={dados.totais.beneficioEconomico} variante="economia" />
                </CelulaDeTotal>
                <CelulaDeTotal numerica>
                  <ValorFiscal valor={dados.totais.valorPago} />
                </CelulaDeTotal>
                <CelulaDeTotal colSpan={3}>
                  <span className="sr-only">Sem total para as colunas descritivas</span>
                </CelulaDeTotal>
              </tr>
            }
          />
        )}

        <NotaDeMetricasIndisponiveis metricasIndisponiveis={dados.metricasIndisponiveis} />
      </section>
    </div>
  );
}
